import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Plus, User, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TeamProject } from '@/types/TeamProject';
import { format } from 'date-fns';
import { useTeamTimeEntries } from '@/hooks/useTeamTimeEntries';
import { TeamTimeEntryDialog } from './TeamTimeEntryDialog';

interface TeamProjectTimeEntriesProps {
  project: TeamProject;
}

export function TeamProjectTimeEntries({ project }: TeamProjectTimeEntriesProps) {
  const { entries, isLoadingEntries, handleAddEntry, handleDeleteEntry } = useTeamTimeEntries(project.id);

  if (isLoadingEntries) {
    return <div>Loading time entries...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Time Entries</h2>
          <p className="text-muted-foreground">
            Track time spent on this project by all team members
          </p>
        </div>
        <TeamTimeEntryDialog 
          project={project} 
          onSubmit={handleAddEntry}
        />
      </div>

      {/* Time Entries List */}
      {entries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No time entries yet</h3>
            <p className="text-muted-foreground mb-4">
              Start tracking time to see entries here.
            </p>
            <TeamTimeEntryDialog 
              project={project} 
              onSubmit={handleAddEntry}
              trigger={
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Log First Entry
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <Card key={entry.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {entry.user_name}
                      </span>
                      <Badge variant={entry.is_billable ? "default" : "secondary"}>
                        {entry.is_billable ? "Billable" : "Non-billable"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {entry.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{format(new Date(entry.date), 'PPP')}</span>
                      {entry.task_category && (
                        <Badge variant="outline" className="text-xs">
                          {entry.task_category}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-4">
                      <div className="font-semibold">
                        {Math.floor(entry.minutes / 60)}h {entry.minutes % 60}m
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteEntry(entry.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}